import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { Flag, AlertCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { STATUS, fmtDate } from "@/lib/status";
import { usePractice } from "@/pages/StaffApp";

export const StatusChip = ({ s }) => {
  const [l, c] = STATUS[s] || [s, "bg-slate-100"];
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${c}`} data-testid={`status-chip-${s}`}>{l}</span>;
};

function BulkBar({ checked, setChecked, onChanged }) {
  const { members } = usePractice();
  const [owner, setOwner] = useState("");
  const run = async (action) => {
    try {
      const { data } = await api.post("/packets/bulk", { action, ids: checked, owner_id: owner });
      const ok = data.results.filter((r) => r.ok).length;
      const bad = data.results.filter((r) => !r.ok);
      toast[bad.length ? "warning" : "success"](`${ok} updated${bad.length ? `, ${bad.length} skipped: ${bad[0].error}` : ""}`);
      setChecked([]);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="p-2 border-b bg-slate-900 text-white flex flex-wrap items-center gap-2 text-sm" data-testid="bulk-action-bar">
      <span className="px-1" data-testid="bulk-selected-count">{checked.length} selected</span>
      <Select value={owner} onValueChange={setOwner}>
        <SelectTrigger className="h-8 w-36 text-slate-900 bg-white" data-testid="bulk-owner-select"><SelectValue placeholder="Assign to..." /></SelectTrigger>
        <SelectContent>{members.filter((m) => m.status === "active").map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" variant="secondary" className="h-8" disabled={!owner} onClick={() => run("assign")} data-testid="bulk-assign-btn">Assign</Button>
      <Button size="sm" variant="secondary" className="h-8" onClick={() => run("flag")} data-testid="bulk-flag-btn">Flag</Button>
      <Button size="sm" variant="secondary" className="h-8" onClick={() => run("archive")} data-testid="bulk-archive-btn">Archive</Button>
      <button className="ml-auto underline text-xs" onClick={() => setChecked([])}>Clear</button>
    </div>
  );
}

export function MoreSentinel({ onMore }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const go = async () => { if (busy) { return; } setBusy(true); try { await onMore(); } finally { setBusy(false); } };
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { go(); } }, { rootMargin: "300px" });
    if (ref.current) { io.observe(ref.current); }
    return () => io.disconnect();
  });
  return <li ref={ref} className="p-3 text-center text-xs text-slate-500" data-testid="packet-list-more">{busy ? "Loading more..." : <button className="underline" onClick={go}>Load more</button>}</li>;
}

function Dates({ p, basis, today }) {
  const late = p.due_date && p.due_date < today && !["signed", "completed"].includes(p.status);
  const created = p.created_at ? fmtDate(format(new Date(p.created_at), "yyyy-MM-dd")) : "-";
  const strong = "font-semibold text-slate-800";
  return (
    <span className="text-[11px] ml-auto text-slate-400 whitespace-nowrap" data-testid={`packet-dates-${p.id}`}>
      <span className={late ? "text-red-700 font-semibold" : basis === "visit" ? strong : ""}>Visit/Due {fmtDate(p.due_date) || "-"}</span>
      <span className="mx-1">&middot;</span><span className={basis === "created" ? strong : ""}>Created {created}</span>
    </span>
  );
}

export default function PacketList({ items, sel, setSel, checked, setChecked, onMore, onChanged, basis }) {
  const today = new Date().toISOString().slice(0, 10);
  const toggle = (id) => setChecked(checked.includes(id) ? checked.filter((x) => x !== id) : [...checked, id]);
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {checked.length > 0 && <BulkBar checked={checked} setChecked={setChecked} onChanged={onChanged} />}
      <ul className="flex-1 overflow-y-auto divide-y" data-testid="packet-list" aria-label="Work items">
        {items.length === 0 && <li className="p-8 text-center text-sm text-slate-500" data-testid="packet-list-empty">No work items in this view.</li>}
        {items.map((p) => (
          <li key={p.id} className={`flex gap-3 px-3 py-3 cursor-pointer transition-colors ${sel === p.id ? "bg-brand-soft border-l-4 border-brand" : "hover:bg-slate-50 border-l-4 border-transparent"}`}
            onClick={() => setSel(p.id)} data-testid={`packet-item-${p.id}`}>
            <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
              <Checkbox checked={checked.includes(p.id)} onCheckedChange={() => toggle(p.id)} aria-label={`Select ${p.patient_name}`} data-testid={`packet-check-${p.id}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {!p.read && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: "var(--brand)" }} aria-label="Unread" />}
                <span className={`truncate ${p.read ? "font-medium" : "font-bold"}`}>{p.patient_name}</span>
                {p.urgency === "high" && <AlertCircle className="w-4 h-4 text-red-600 shrink-0" aria-label="High urgency" />}
                {p.flagged && <Flag className="w-4 h-4 text-orange-500 fill-orange-500 shrink-0" aria-label="Flagged" />}
                {p.snoozed && <Clock className="w-4 h-4 text-slate-400 shrink-0" aria-label="Snoozed" />}
              </div>
              <p className="text-xs text-slate-500 truncate mt-0.5">{p.title}</p>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <StatusChip s={p.status} />
                {p.signer?.type === "representative" && <span className="text-[11px] text-purple-700">Representative</span>}
                <Dates p={p} basis={basis} today={today} />
              </div>
            </div>
          </li>
        ))}
        {onMore && <MoreSentinel onMore={onMore} />}
      </ul>
    </div>
  );
}
